/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useRef, useEffect, useCallback } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { useStaticDataContext } from '../../modules/context';
import { SearchFormState, useSearchFormStore } from '../../modules/store/searchFormStore';
import { useConditionalPageContextSERP } from '../../hooks';
import { SERP_VALIDATE_URL } from '../../modules/constants';
import { Button, ButtonTypeVariation, useCheckBreakpoint } from '@marriott/mi-ui-library';
import {
  convertStateValuestoFormValues,
  eventUtil,
  formSubmitSearchRequestParams,
  propertySearchUXLCallHandler,
  updateRecentSearched,
} from '../../modules/utils';
import {
  CALL_SESSION_DATA_EVENT,
  searchFormClickTrackingLoc,
  TAX_AND_CURRENCY_SESSION_UPDATE_EVENT,
} from '../../constants/lib/constants';
import { ADF_SEARCH_CTA_ENDPOINT_URL, SearchFormActiveState } from '../../constants';
import clsx from 'clsx';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import {
  isConcurFlowActive,
  extractConcurCode,
  setConcurUserOverride,
  isTAGPCodes,
  maxRoomCountForTA,
} from '../../modules/utils/searchFormHelper';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { isRateListMenuPage } from '../../utils';
import {
  getTaxAndCurrencySnapshot,
  resolveTaxAndCurrency,
  TaxAndCurrencyType,
  updateSessionDoc,
} from '../../modules/utils/ngsfHelper';

export const SearchFormCTAGroup: FC<{
  customClass?: string;
  lastElementRef?: any;
  sessionData?: any;
  isAvailabilityScenario?: boolean;
  setMobileFormModalState?: (state: any) => void;
  isMobileDropdownForm?: boolean;
  isInModal?: boolean;
  isNextGenHP?: boolean;
  setCurrentFormState?: (state: any) => void;
}> = ({
  customClass,
  lastElementRef,
  sessionData,
  isAvailabilityScenario,
  setMobileFormModalState,
  isInModal,
  isNextGenHP,
  setCurrentFormState,
}) => {
  const searchformRef = useRef(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const { envVarsObject } = useClientEnvVarsStore();
    const {
    localDateFormat,
    searchCTAEndpointURL,
    submitCTA,
    cancelLabel,
    nextGenEditLabel,
    restrictRoomCount = 'false',
  } = useStaticDataContext();
  const shopStoreInitialization = useSearchFormStore((state: SearchFormState) => state.shopStoreInitialization);

  const { currentLocale } = useConditionalPageContextSERP();
  /**
   * return search form submit button for mobile/desktop view
   */
  const destination = useSearchFormStore((state: any) => state?.destination);
  const destinationHiddenFields = useSearchFormStore((state: any) => state?.destinationHiddenFields);
  const roomsAndGuests = useSearchFormStore((state: any) => state?.roomsAndGuests);
  const dates = useSearchFormStore((state: any) => state?.dates);
  const usePoints = useSearchFormStore((state: any) => state?.usePoints);
  const brands = useSearchFormStore((state: any) => state?.brands);
  const specialRates = useSearchFormStore((state: any) => state?.specialRates);
  const destinationFieldDetails = useSearchFormStore((state: any) => state?.destinationFieldDetails);
  const resortsHiddenFields = useSearchFormStore((state: any) => state?.resortsHiddenFields);
  const isSearchFormActiveState = useSearchFormStore((state: any) => state?.isSearchFormActiveState);
  const isInvalidCorpCode = useSearchFormStore((state: any) => state?.isInvalidCorpCode);
  const marriottBrands = brands?.marriottBrands && brands?.marriottBrands;

  const pageContext = useConditionalPageContextSERP();
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;
  const featureFlagSerpEnableGeographicSearch =
    featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ENABLE_GEOGRAPHIC_SEARCH] ?? false;
  const { preHeader, interceptorResponse, NEXT_PUBLIC_PRE_PROCESSING_URL } = pageContext;
  const isDesktopView = useCheckBreakpoint('viewportL');

  const setSearchCTAClicked = useSearchFormStore((state: any) => state?.setSearchCTAClicked);
  const setIsSearchFormActiveState = useSearchFormStore((state: any) => state?.setIsSearchFormActiveState);
  const userDetailsState = useUserDetailsGlobalStore();
  const isOnRateListMenuPage = isRateListMenuPage();
  const formValues = convertStateValuestoFormValues(
    destination,
    roomsAndGuests,
    dates,
    usePoints,
    specialRates,
    destinationHiddenFields
  );
  const isFlexibleDateSearch = dates?.flexible || (!dates?.fromDate && !dates?.toDate);

  const taxAndCurrencyRef = useRef<TaxAndCurrencyType | undefined>(getTaxAndCurrencySnapshot());

  const syncSessionDoc = useCallback(() => {
    updateSessionDoc(formValues, isFlexibleDateSearch, resolveTaxAndCurrency(taxAndCurrencyRef.current));
  }, [formValues, isFlexibleDateSearch]);

  useEffect(() => {
    taxAndCurrencyRef.current = getTaxAndCurrencySnapshot();

    const handleTaxAndCurrencyUpdate = (data: TaxAndCurrencyType) => {
      taxAndCurrencyRef.current = data;
    };

    eventUtil.on(TAX_AND_CURRENCY_SESSION_UPDATE_EVENT, handleTaxAndCurrencyUpdate);
    eventUtil.on(CALL_SESSION_DATA_EVENT, syncSessionDoc);

    return (): void => {
      eventUtil.remove(TAX_AND_CURRENCY_SESSION_UPDATE_EVENT, () => {});
      eventUtil.remove(CALL_SESSION_DATA_EVENT, () => {});
    };
  }, [syncSessionDoc]);

  const getRooms = roomsAndGuests?.numRooms;

  const isTagpRoomCountExceeded = (): boolean => {
    if (!isOnRateListMenuPage) return false; // this restrict TAGP flow to Rate List Menu page
    const corpCode = specialRates?.corporateCode ?? '';
    const roomCount = Number(getRooms?.toString()?.split(/[-~+]/)[0]);
    return (
      isTAGPCodes(corpCode, restrictRoomCount) && roomCount > Number(maxRoomCountForTA(corpCode))
    );
  }
  const updateSearchBtn = (): void => {
    if (getRooms === '10-25' || getRooms === '26+') {
      handleSubmit();
      return;
    }
    // if (submitAction === `publish-user-selection`) { // need to add it from AEM
    if (getRooms === '4-9') {
      eventUtil.dispatch('bannerMessage', { displayBanner: true });
      document.dispatchEvent(
        new CustomEvent('showBannerMessage', {
          detail: { displayBanner: true, bannerMessage: `${'storefrontPropertySearchWidgetErrorMessageRooms'}` },
        })
      );
    } else {
      eventUtil.dispatch('bannerMessage', { displayBanner: false });
      document.dispatchEvent(
        new CustomEvent('showBannerMessage', {
          detail: { displayBanner: false, bannerMessage: `${'storefrontPropertySearchWidgetErrorMessageRooms'}` },
        })
      );
    }
    // }

    // if (submitAction === `publish-user-selection` && getRooms !== '10-25' && getRooms !== '26+') { // need to add submitAction === `publish-user-selection` && after implementation from AEM
    if (getRooms !== '10-25' && getRooms !== '26+') {
      syncSessionDoc();
    }
  };

  /**
   * return button
   */
  const handleSubmit = () => {
    // Track user override if they're changing special rates after Concur was applied
    const freshFeatureFlags = useClientEnvVarsStore.getState().envVarsObject['featureFlags'] as unknown as Record<
      string,
      FLAG_TYPE
    >;
    if (
      isConcurFlowActive(
        freshFeatureFlags,
        sessionData?.sessionData?.data || sessionData?.sessionData?.cacheData?.data
      ) &&
      userDetailsState?.isUserDetailsSaved
    ) {
      const concurCode = extractConcurCode(userDetailsState);
      const currentSpecialRate = specialRates?.clusterCode;
      const currentCorpCode = specialRates?.corporateCode;

      // If user has changed from Concur code, set the override flag
      if (concurCode && concurCode !== 'none' && concurCode !== '') {
        const isStillUsingConcur = currentSpecialRate === 'corp' && currentCorpCode === concurCode;
        if (!isStillUsingConcur) {
          setConcurUserOverride();
        }
      }
    }

    if (typeof window !== 'undefined') {
      const offersHiddenCountry = window.sessionStorage.getItem('offersHiddenCountrySuggestion');
      window.sessionStorage.setItem('offersHiddenCountry', offersHiddenCountry ?? '');
    }
    if (
      destination.displayText !==
      sessionData?.sessionData?.cacheData?.data?.AriesSearch?.searchCriteria?.address?.destination
    ) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('OffersfilterData', JSON.stringify({}));
      }
    }
    formSubmitSearchRequestParams(
      destination,
      destinationHiddenFields,
      roomsAndGuests,
      dates,
      usePoints,
      marriottBrands,
      specialRates,
      destinationFieldDetails,
      resortsHiddenFields,
      searchformRef,
      envVarsObject?.['NEXT_PUBLIC_SUBMIT_SEARCH_URL'],
      envVarsObject?.['NEXT_PUBLIC_AVAILABILITY_SEARCH_URL'],
      undefined,
      currentLocale ?? envVarsObject?.['ACCEPT_LANGUAGE'],
      localDateFormat,
      sessionData,
      isAvailabilityScenario,
      false,
      false,
      pageContext?.headersData,
      true
    );
  };

  const handleADFSubmit = () => {
    // Track user override if they're changing special rates after Concur was applied
    const freshFeatureFlags = useClientEnvVarsStore.getState().envVarsObject['featureFlags'] as unknown as Record<
      string,
      FLAG_TYPE
    >;
    if (
      isConcurFlowActive(
        freshFeatureFlags,
        sessionData?.sessionData?.data || sessionData?.sessionData?.cacheData?.data
      ) &&
      userDetailsState?.isUserDetailsSaved
    ) {
      const concurCode = extractConcurCode(userDetailsState);
      const currentSpecialRate = specialRates?.clusterCode;
      const currentCorpCode = specialRates?.corporateCode;

      // If user has changed from Concur code, set the override flag
      if (concurCode && concurCode !== 'none' && concurCode !== '') {
        const isStillUsingConcur = currentSpecialRate === 'corp' && currentCorpCode === concurCode;
        if (!isStillUsingConcur) {
          setConcurUserOverride();
        }
      }
    }

    setSearchCTAClicked?.(true);
    setMobileFormModalState?.(false);
  };
  const handlePropertySearchRequest = () => {
    const flexible = dates?.flexible || (!dates?.fromDate && !dates?.toDate);
    const params = formSubmitSearchRequestParams(
      destination,
      destinationHiddenFields,
      roomsAndGuests,
      dates,
      usePoints,
      marriottBrands,
      specialRates,
      destinationFieldDetails,
      resortsHiddenFields,
      searchformRef,
      envVarsObject?.['NEXT_PUBLIC_SUBMIT_SEARCH_URL'],
      envVarsObject?.['NEXT_PUBLIC_AVAILABILITY_SEARCH_URL'],
      undefined,
      currentLocale ?? envVarsObject?.['ACCEPT_LANGUAGE'],
      localDateFormat,
      sessionData,
      false,
      false,
      true, //skipPerformSubmitSearchFormAction
      pageContext?.headersData,
      true
    );
    const formValues = convertStateValuestoFormValues(
      destination,
      roomsAndGuests,
      dates,
      usePoints,
      specialRates,
      destinationHiddenFields
    );
    propertySearchUXLCallHandler(
      formValues,
      flexible,
      dates?.numberOfNights,
      false, //TODO - isAutoSuggestSelected - use exact value when migrating context to zustand
      false, //TODO - isRecentSearchSelected - use exact value when migrating context to zustand
      params,
      NEXT_PUBLIC_PRE_PROCESSING_URL,
      sessionData,
      interceptorResponse,
      preHeader,
      pageContext?.currentLocale,
      pageContext?.headersData,
      featureFlagSerpEnableGeographicSearch
    );
    updateRecentSearched({
      destination,
      destinationHiddenFields,
      roomsAndGuests,
      dates,
      usePoints,
      specialRates,
    });
  };

  // Add keyboard event listener to Edit button
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        // Focus the first focusable input in the search form after activating edit state
        setTimeout(() => {
          // Find the search form container first, then look for the first focusable input within it
          const searchFormContainer = document.querySelector(
            '.document_search_form_container, .primary-wrapper'
          ) as HTMLElement;

          if (searchFormContainer) {
            // Look for the first non-readonly input within the search form (destination is first and editable)
            const firstEditableInput = searchFormContainer.querySelector(
              'input:not([disabled]):not([readonly]):not([tabindex="-1"]):not([type="hidden"])'
            ) as HTMLInputElement;

            if (firstEditableInput) {
              firstEditableInput.focus();
            }
          }
        }, 100);
      }
    };

    const buttonElement = editButtonRef.current;
    if (buttonElement) {
      buttonElement.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (buttonElement) {
        buttonElement.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [isSearchFormActiveState]);

  const isGroupBooking = getRooms === '10-25' || getRooms === '26+';

  const getButtonType = () => {
  if (!isOnRateListMenuPage) return ButtonTypeVariation.Button;
  return isGroupBooking
    ? ButtonTypeVariation.Submit
    : ButtonTypeVariation.Button;
  };

  // Add keyboard event listener to Cancel button
  useEffect(() => {
    const handleCancelKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        // Focus the Edit button after canceling (form becomes inactive)
        setTimeout(() => {
          if (editButtonRef.current) {
            editButtonRef.current.focus();
          }
        }, 100);
      }
    };

    const cancelButtonElement = cancelButtonRef.current;
    if (cancelButtonElement) {
      cancelButtonElement.addEventListener('keydown', handleCancelKeyDown);
    }

    return () => {
      if (cancelButtonElement) {
        cancelButtonElement.removeEventListener('keydown', handleCancelKeyDown);
      }
    };
  }, [isSearchFormActiveState]);

  const handleSearchSubmit = (): void => {

    if (isTagpRoomCountExceeded()) {
      return;
    }

    switch (true) {
      // Rate List Menu–specific behavior
      case isOnRateListMenuPage:
        updateSearchBtn();
        break;

      // ADF flow
      case searchCTAEndpointURL === ADF_SEARCH_CTA_ENDPOINT_URL:
        handleADFSubmit();
        break;

      // SERP validate flow
      case searchCTAEndpointURL === SERP_VALIDATE_URL:
        handlePropertySearchRequest();
        break;

      // Default submit behavior
      default:
        handleSubmit();
        break;
    }

    // Reset form state & close modal
    setIsSearchFormActiveState?.(false);
    setMobileFormModalState?.(false);
  };

  return (
    <div className={customClass}>
      {/* Edit button - only shown for NextGen (standard) when not in active/edit state */}
      {!isNextGenHP && !isSearchFormActiveState && !isInModal ? (
        <Button
          {...{ custom_click_track_value: `${searchFormClickTrackingLoc} | ${nextGenEditLabel} |internal` }}
          className={clsx(
            'd-flex align-items-center justify-content-center m-button-primary-cancel edit-cta-btn custom_click_track standard'
          )}
          ariaLabel={nextGenEditLabel}
          ref={editButtonRef}
          callback={() => {
            if (isDesktopView) {
              setIsSearchFormActiveState?.(true);
            } else {
              setCurrentFormState?.(SearchFormActiveState.FORM_STATE);
              setMobileFormModalState?.(true);
            }
          }}
        >
          <span className="icon-edit" />
          <span className="d-none d-lg-block">{nextGenEditLabel}</span>
        </Button>
      ) : (
        <div className={clsx('align-items-center cta-button-group d-flex', isInModal && 'col-12')}>
          {/* Cancel CTA */}
          {!isNextGenHP && (
            <Button
              {...{ custom_click_track_value: `${searchFormClickTrackingLoc} | ${cancelLabel} |internal` }}
              className={clsx(
                'd-none d-lg-flex justify-content-center align-items-center m-button-primary-cancel cancel-cta-btn custom_click_track standard'
              )}
              ariaLabel={cancelLabel}
              ref={cancelButtonRef}
              callback={() => {
                if (!isInvalidCorpCode) {
                  shopStoreInitialization(sessionData?.sessionData, {});
                }
                setIsSearchFormActiveState?.(false);
              }}
            >
              {cancelLabel}
            </Button>
          )}

          {/* Submit CTA */}
          {isNextGenHP && !isInModal && !isDesktopView ? (
            <></>
          ) : (
            <Button
              type={getButtonType()}
              {...{ custom_click_track_value: `${searchFormClickTrackingLoc} | ${submitCTA} |internal` }}
              ref={lastElementRef}
              className={clsx(
                'd-flex py-0 px-3 px-lg-4 justify-content-center align-items-center m-button-primary-icon ',
                !isNextGenHP ? 'update-cta' : 'update-cta-hp',
                'custom_click_track',
                'standard'
              )}
              ariaLabel={submitCTA}
              callback={handleSearchSubmit}
            >
              {isNextGenHP && <span className="icon-search" />}
              {submitCTA}
            </Button>
          )}
          <form ref={searchformRef} id={Date.now().toString() + '-submit-search-form-action'} method="get"></form>
        </div>
      )}
    </div>
  );
};
